// useTableState — one hook that puts a table's query state in the URL.
//
// WHY THE URL. Admin work is collaborative and interrupted: staff paste views
// to each other ("the failed invoices this month"), reload after an edit, and
// use browser back. State held only in React loses all three — the old admin
// reset every filter on every reload.
//
// WHY NOT TanStack `validateSearch`. That would require every one of the 24
// route files to declare a search schema before it could host a table. This
// hook reads and writes `window.location.search` directly and pushes through
// the History API, so ANY route can adopt it with one line and no route-level
// ceremony. The router still sees the change (it listens to history), so
// navigation, back/forward and route reloads all behave.
//
// Keys are namespaced per table (`prefix`), so two tables on one page — e.g.
// invoices and refunds — cannot collide.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import type { SortDirection } from './DataTable';

export interface TableState {
  /** Free-text search. */
  q: string;
  /** Faceted filters, e.g. `{ status: 'paid', currency: 'QAR' }`. */
  filters: Record<string, string>;
  sort: { key: string; direction: SortDirection } | null;
  page: number;
  pageSize: number;
}

export interface UseTableStateOptions {
  /** Namespace for the URL keys. Required when a page hosts two tables. */
  prefix?: string;
  defaultSort?: { key: string; direction: SortDirection } | null;
  defaultPageSize?: number;
  /** Filter keys this table understands. Unknown params are left untouched. */
  filterKeys?: readonly string[];
}

export interface TableStateApi extends TableState {
  setQuery: (q: string) => void;
  setFilter: (key: string, value: string | null) => void;
  clearFilters: () => void;
  setSort: (sort: { key: string; direction: SortDirection }) => void;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  /** True when anything is narrowing the result set — drives "Clear all". */
  isFiltered: boolean;
}

const DEFAULT_PAGE_SIZE = 25;

function readSearch(): URLSearchParams {
  if (typeof window === 'undefined') return new URLSearchParams();
  return new URLSearchParams(window.location.search);
}

export function useTableState(options: UseTableStateOptions = {}): TableStateApi {
  const {
    prefix = '',
    defaultSort = null,
    defaultPageSize = DEFAULT_PAGE_SIZE,
    filterKeys = [],
  } = options;

  const key = useCallback((name: string) => (prefix ? `${prefix}_${name}` : name), [prefix]);

  // Mirror of the URL. Re-read on popstate so browser back/forward works.
  const [search, setSearch] = useState<URLSearchParams>(readSearch);

  useEffect(() => {
    function onPop() {
      setSearch(readSearch());
    }
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  // `filterKeys` is almost always an inline array literal, so a new identity
  // every render. Join it to a stable string for the dependency lists below,
  // otherwise every memo here recomputes on every render.
  const filterKeysToken = filterKeys.join(',');

  const write = useCallback((mutate: (params: URLSearchParams) => void) => {
    const next = readSearch();
    mutate(next);
    const qs = next.toString();
    const url = `${window.location.pathname}${qs ? `?${qs}` : ''}${window.location.hash}`;
    // replaceState, not pushState: typing in a search box must not create a
    // history entry per keystroke. Back should leave the page, not undo
    // letters one at a time.
    window.history.replaceState(window.history.state, '', url);
    setSearch(next);
  }, []);

  const q = search.get(key('q')) ?? '';

  const filters = useMemo(() => {
    const out: Record<string, string> = {};
    for (const name of filterKeysToken ? filterKeysToken.split(',') : []) {
      const value = search.get(key(name));
      if (value) out[name] = value;
    }
    return out;
  }, [search, key, filterKeysToken]);

  const sort = useMemo(() => {
    const raw = search.get(key('sort'));
    if (!raw) return defaultSort;
    // Encoded as `key:direction` — one param instead of two, and it reads
    // clearly in a pasted URL.
    const [sortKey, dir] = raw.split(':');
    if (!sortKey) return defaultSort;
    return { key: sortKey, direction: dir === 'asc' ? 'asc' : 'desc' } as const;
  }, [search, key, defaultSort]);

  const page = useMemo(() => {
    const raw = Number(search.get(key('page')) ?? '1');
    return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : 1;
  }, [search, key]);

  const pageSize = useMemo(() => {
    const raw = Number(search.get(key('size')) ?? String(defaultPageSize));
    return Number.isFinite(raw) && raw >= 1 ? Math.floor(raw) : defaultPageSize;
  }, [search, key, defaultPageSize]);

  // Any change that narrows the result set must reset to page 1 — otherwise
  // filtering from page 7 lands on an empty page and reads as "no results".
  const setQuery = useCallback(
    (value: string) => {
      write((p) => {
        if (value) p.set(key('q'), value);
        else p.delete(key('q'));
        p.delete(key('page'));
      });
    },
    [write, key],
  );

  const setFilter = useCallback(
    (name: string, value: string | null) => {
      write((p) => {
        if (value) p.set(key(name), value);
        else p.delete(key(name));
        p.delete(key('page'));
      });
    },
    [write, key],
  );

  const clearFilters = useCallback(() => {
    write((p) => {
      p.delete(key('q'));
      for (const name of filterKeysToken ? filterKeysToken.split(',') : []) p.delete(key(name));
      p.delete(key('page'));
    });
  }, [write, key, filterKeysToken]);

  const setSort = useCallback(
    (next: { key: string; direction: SortDirection }) => {
      write((p) => {
        p.set(key('sort'), `${next.key}:${next.direction}`);
        p.delete(key('page'));
      });
    },
    [write, key],
  );

  const setPage = useCallback(
    (next: number) => {
      write((p) => {
        if (next <= 1) p.delete(key('page'));
        else p.set(key('page'), String(next));
      });
    },
    [write, key],
  );

  const setPageSize = useCallback(
    (size: number) => {
      write((p) => {
        if (size === defaultPageSize) p.delete(key('size'));
        else p.set(key('size'), String(size));
        p.delete(key('page'));
      });
    },
    [write, key, defaultPageSize],
  );

  return {
    q,
    filters,
    sort,
    page,
    pageSize,
    setQuery,
    setFilter,
    clearFilters,
    setSort,
    setPage,
    setPageSize,
    isFiltered: q !== '' || Object.keys(filters).length > 0,
  };
}

/**
 * Debounce a rapidly-changing value (a search box) before it reaches a query.
 * Without this, every keystroke is a request.
 */
export function useDebounced<T>(value: T, delayMs = 300): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(t);
  }, [value, delayMs]);
  return debounced;
}

/**
 * Client-side slice for endpoints that return a full list. Server-paginated
 * endpoints ignore this and pass their own total.
 *
 * Kept deliberately separate from useTableState: paginating in the client is a
 * property of the DATA SOURCE, not of the URL state, and conflating them is how
 * you end up with a table that silently shows page 1 of a server page 3.
 */
export function paginate<T>(rows: readonly T[], page: number, pageSize: number): T[] {
  const start = (page - 1) * pageSize;
  return rows.slice(start, start + pageSize);
}

/** Stable sort for client-side sorting. Returns a new array. */
export function sortRows<T>(
  rows: readonly T[],
  sort: { key: string; direction: SortDirection } | null,
  accessors: Record<string, (row: T) => string | number | null | undefined>,
): T[] {
  if (!sort) return [...rows];
  const accessor = accessors[sort.key];
  if (!accessor) return [...rows];
  const factor = sort.direction === 'asc' ? 1 : -1;

  return [...rows].sort((a, b) => {
    const av = accessor(a);
    const bv = accessor(b);
    // Nulls sort last regardless of direction — an unpaid invoice with no
    // paid-at date should not lead a "most recently paid" list.
    if (av == null && bv == null) return 0;
    if (av == null) return 1;
    if (bv == null) return -1;
    if (typeof av === 'number' && typeof bv === 'number') return (av - bv) * factor;
    return String(av).localeCompare(String(bv), 'en', { numeric: true }) * factor;
  });
}

/** Ref-stable callback for effects that must not re-run when a handler changes. */
export function useEventCallback<A extends unknown[], R>(fn: (...args: A) => R): (...args: A) => R {
  const ref = useRef(fn);
  useEffect(() => {
    ref.current = fn;
  });
  return useCallback((...args: A) => ref.current(...args), []);
}
